import { urls } from '@repo/app-constants';
import { DOMAINS, IMPORTANCE, SECTIONS } from '@repo/monitoring';
import { AddonFlags, PractitionerKitRequest, StripeLineItem } from '@repo/types';
import { queryOptions } from '@tanstack/react-query';
import { createServerFn } from '@tanstack/react-start';

import { API } from '~/api';
import { DISPLAY_NAMES } from './usePractitionerKitsForPatient';

// Addon lookup_code -> display label. Post-cutover shape (ENGG-589) carries
// each add-on as its own line identified by lookup_code, replacing the
// pre-cutover AddonFlags JSON blob this map's keys still mirror.
const ADDON_DISPLAY_LABEL: { [lookupCode: string]: string } = {
  QPCR_CANDIDA: 'Candida qPCR',
  QPCR_HPYLORI: 'H. pylori qPCR',
  AG_HPYLORI: 'H. pylori AG',
};

const formatKitDisplayTitleLegacy = (line_items: StripeLineItem[]): string => {
  // Group items by base product and collect addons
  const productGroups = new Map<
    string,
    {
      name: string;
      quantity: number;
      addons: Set<string>;
    }
  >();

  line_items.forEach(item => {
    const { lookup_code, addons } = item.price_data.product_data!.metadata;
    // Get display name for the product
    const productName =
      DISPLAY_NAMES[lookup_code] || item.price_data.product_data!.name;
    // Parse addons if they exist
    let parsedAddons: AddonFlags = {};
    if (addons) {
      try {
        parsedAddons = JSON.parse(addons);
      } catch (e) {
        console.warn('Failed to parse addons:', addons);
      }
    }
    // Get or create product group
    if (!productGroups.has(lookup_code)) {
      productGroups.set(lookup_code, {
        name: productName,
        quantity: 0,
        addons: new Set<string>(),
      });
    }
    const group = productGroups.get(lookup_code)!;
    group.quantity += parseInt(item.quantity);
    // Add addon names to the set
    if (parsedAddons.qpcr_candida) {
      group.addons.add('Candida qPCR');
    }
    if (parsedAddons.qpcr_hpylori) {
      group.addons.add('H. pylori qPCR');
    }
    if (parsedAddons.ag_hpylori) {
      group.addons.add('H. pylori AG');
    }
  });

  // Build summary string
  const summaryParts: string[] = [];

  productGroups.forEach(group => {
    let itemSummary = group.name;
    // Add quantity if > 1
    if (group.quantity > 1) {
      itemSummary = `${group.quantity}x ${itemSummary}`;
    }
    // Add addons if any
    if (group.addons.size > 0) {
      const addonsList = Array.from(group.addons).join(' & ');
      itemSummary += ` + ${addonsList}`;
    }
    summaryParts.push(itemSummary);
  });

  return summaryParts.join(', ');
};

// Post-cutover shape (ENGG-589): each kit and each selected add-on is its
// own line, correlated by `group_id` instead of a per-kit addons JSON blob.
const formatKitDisplayTitleCatalog = (line_items: StripeLineItem[]): string => {
  const productGroups = new Map<
    string,
    { name: string; quantity: number; addons: Set<string> }
  >();

  for (const item of line_items) {
    if (item.metadata?.role !== 'kit') continue;
    const { group_id, lookup_code } = item.metadata;
    productGroups.set(group_id, {
      name: (lookup_code && DISPLAY_NAMES[lookup_code]) || lookup_code || 'Kit',
      quantity: parseInt(item.quantity, 10),
      addons: new Set<string>(),
    });
  }

  for (const item of line_items) {
    if (item.metadata?.role !== 'addon') continue;
    const { group_id, lookup_code } = item.metadata;
    const group = productGroups.get(group_id);
    if (group && lookup_code) {
      group.addons.add(ADDON_DISPLAY_LABEL[lookup_code] || lookup_code);
    }
  }

  const summaryParts: string[] = [];
  productGroups.forEach(group => {
    let itemSummary = group.name;
    if (group.quantity > 1) {
      itemSummary = `${group.quantity}x ${itemSummary}`;
    }
    if (group.addons.size > 0) {
      itemSummary += ` + ${Array.from(group.addons).join(' & ')}`;
    }
    summaryParts.push(itemSummary);
  });

  return summaryParts.join(', ');
};

/**
 * Generates an order summary string for kit requests.
 * Handles both Shopify (single kit) and Stripe (multiple kits with addons) orders.
 */
const formatKitDisplayTitle = (request: PractitionerKitRequest): string => {
  // If no stripe_checkout_json, use legacy Shopify single kit logic
  if (!request.stripe_checkout_json) {
    return (
      DISPLAY_NAMES[request.shopify_product?.lookup_code || ''] ||
      request.shopify_product?.name ||
      'Unknown Kit'
    );
  }

  // Handle Stripe orders with multiple kits and addons
  const { line_items } = request.stripe_checkout_json;
  if (!line_items || line_items.length === 0) {
    return 'No items';
  }

  // A single stored payload is always homogeneous — it was built once, under
  // one flag state — so the first line item's shape tells us the rest.
  const isCatalogProductShape = !!line_items[0]?.metadata?.role;

  return isCatalogProductShape
    ? formatKitDisplayTitleCatalog(line_items)
    : formatKitDisplayTitleLegacy(line_items);
};

export const getKitRequests = createServerFn({
  method: 'GET',
  response: 'data',
})
  .validator((email?: string) => email)
  .handler(async () => {
    const response = await API.get(
      `${urls.apiBasePath}/practitioners/patient-invoices`,
      {},
      true,
      undefined,
      {
        tags: {
          section: SECTIONS.INVOICES,
          domain: DOMAINS.PRACTITIONERS,
          importance: IMPORTANCE.HIGH,
        },
        context: {},
      },
    );
    return response?.result;
  });

export const kitRequestsQueryOptions = (isAuthenticated: boolean) =>
  queryOptions({
    queryKey: ['kitRequests', isAuthenticated],
    queryFn: () => (isAuthenticated ? getKitRequests() : Promise.resolve([])),
    enabled: isAuthenticated,
    select: (data: PractitionerKitRequest[] | null) => {
      if (!data) return [];
      return data.map(request => {
        const full_name = `${request.customer_first_name} ${request.customer_last_name}`;
        const kit_display_title = formatKitDisplayTitle(request);
        return { ...request, full_name, kit_display_title };
      });
    },
  });
